import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Slide, Fade, Tabs, Tab, Tooltip } from "@mui/material";
import DataTable from "react-data-table-component";
import { MdCheckCircle, MdError, MdWarning, MdInfo, MdCode, MdFolder } from "react-icons/md";
import { FaServer, FaDatabase, FaWindows, FaGithub, FaGitlab, FaBitbucket, FaMicrosoft } from "react-icons/fa";

import BreadcrumbHeading from "../../../common/RDTListHeading/BreadcrumbHeading";
import ListHeading from "../../../common/RDTListHeading/ListHeading";
import Images from "../../../../utils/images";
import { changeTitle } from "../../../../state/slices/header";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { getScanDetails, getScannedFiles } from "../../../../api/credentialScanning";
import "../index.scss";

// ── Status config ──────────────────────────────────────────────────────────

// Style/icon mappings keyed by stable wire identifier. The display label is
// resolved by t() at render time (see resolveStatusLabel).
const STATUS_CONFIG = {
  success:           { labelKey: "credentialScanning.tgtStatusScanned",          color: "#2e7d32", bg: "#e8f5e9", Icon: MdCheckCircle },
  no_files:          { labelKey: "credentialScanning.tgtStatusNoFilesFound",     color: "#1565c0", bg: "#e3f2fd", Icon: MdInfo },
  no_credentials:    { labelKey: "credentialScanning.tgtStatusNoCredentials",    color: "#e65100", bg: "#fff3e0", Icon: MdWarning },
  connection_failed: { labelKey: "credentialScanning.tgtStatusConnectionFailed", color: "#c62828", bg: "#ffebee", Icon: MdError },
  auth_failed:       { labelKey: "credentialScanning.tgtStatusAuthFailed",       color: "#c62828", bg: "#ffebee", Icon: MdError },
  unsupported:       { labelKey: "credentialScanning.tgtStatusUnsupported",      color: "#757575", bg: "#f5f5f5", Icon: MdInfo },
  error:             { labelKey: "credentialScanning.tgtStatusError",            color: "#c62828", bg: "#ffebee", Icon: MdError },
};

const SCAN_STATUS_STYLES = {
  PENDING:     { bg: "#e3f2fd", color: "#1565c0" },
  IN_PROGRESS: { bg: "#fff8e1", color: "#f57f17" },
  COMPLETED:   { bg: "#e8f5e9", color: "#2e7d32" },
  FAILED:      { bg: "#ffebee", color: "#c62828" },
  CANCELLED:   { bg: "#f5f5f5", color: "#757575" },
};

// Brand names (GitHub/GitLab/Bitbucket/Azure DevOps) stay verbatim across
// locales. Protocol labels resolve through t() — `labelKey` when set,
// `label` otherwise.
const PIPELINE_TYPE_CONFIG = {
  ssh:          { labelKey: "credentialScanning.pipelineSshSftp",    color: "#1565c0", Icon: FaServer },
  smb:          { labelKey: "credentialScanning.pipelineWindowsSmb", color: "#6a1b9a", Icon: FaWindows },
  database:     { labelKey: "credentialScanning.sourceDatabase",     color: "#1b5e20", Icon: FaDatabase },
  github:       { label: "GitHub",        color: "#24292e", Icon: FaGithub },
  gitlab:       { label: "GitLab",        color: "#e24329", Icon: FaGitlab },
  bitbucket:    { label: "Bitbucket",     color: "#0052cc", Icon: FaBitbucket },
  azure_devops: { label: "Azure DevOps",  color: "#0078d4", Icon: FaMicrosoft },
};

const TARGET_ICONS = {
  ssh:          <FaServer     size={13} style={{ color: "#546e7a" }} />,
  smb:          <FaWindows    size={13} style={{ color: "#546e7a" }} />,
  database:     <FaDatabase   size={13} style={{ color: "#546e7a" }} />,
  github:       <FaGithub    size={13} style={{ color: "#546e7a" }} />,
  gitlab:       <FaGitlab    size={13} style={{ color: "#e24329" }} />,
  bitbucket:    <FaBitbucket size={13} style={{ color: "#0052cc" }} />,
  azure_devops: <FaMicrosoft size={13} style={{ color: "#0078d4" }} />,
};

const getTargetIcon = (target) => {
  if (target.target_type === "git_repo") {
    return TARGET_ICONS[target.provider] || <MdCode size={13} />;
  }
  return TARGET_ICONS[target.target_type] || <FaServer size={13} />;
};

const resolveLabel = (cfg, t) => (cfg?.labelKey ? t(cfg.labelKey) : cfg?.label);

const StatusChip = ({ statusKey }) => {
  const { t } = useTranslation();
  const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.error;
  const { Icon } = cfg;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: cfg.bg, color: cfg.color,
      padding: "3px 10px", borderRadius: 4, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      <Icon size={13} />
      {resolveLabel(cfg, t)}
    </span>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────

const _fmt = (secs) => {
  if (secs == null) return "—";
  if (secs < 60) return `${Math.round(secs)}s`;
  return `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`;
};

function groupTargets(targets) {
  const pamGroups = {};
  const gitGroups = {};
  targets.forEach((t) => {
    if (t.target_type === "git_repo") {
      const k = t.provider || "unknown";
      if (!gitGroups[k]) gitGroups[k] = [];
      gitGroups[k].push(t);
    } else {
      const k = t.target_type;
      if (!pamGroups[k]) pamGroups[k] = [];
      pamGroups[k].push(t);
    }
  });
  return { pamGroups, gitGroups };
}

function countStatus(targets) {
  return targets.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});
}

// ── Flow Diagram — Pipeline header ─────────────────────────────────────────

const ConnectorArrow = () => (
  <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
    <div style={{ width: 20, height: 2, background: "#c5cae9" }} />
    <div style={{ width: 0, height: 0, borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderLeft: "6px solid #c5cae9" }} />
  </div>
);

const PipelineStageNode = ({ label, Icon, color, count, okCount, failCount, isLast }) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    <div style={{
      background: "#fff",
      border: `2px solid ${color}22`,
      borderTop: `3px solid ${color}`,
      borderRadius: 10,
      padding: "10px 16px",
      minWidth: 130,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
        <span style={{ color, fontSize: 14 }}><Icon size={14} /></span>
        <span style={{ fontWeight: 700, fontSize: 12, color: "#333" }}>{label}</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontWeight: 800, fontSize: 20, color, lineHeight: 1 }}>{count}</span>
        <div style={{ fontSize: 10, lineHeight: 1.4 }}>
          {okCount > 0 && <div style={{ color: "#2e7d32" }}>✓ {okCount} ok</div>}
          {failCount > 0 && <div style={{ color: "#c62828" }}>✗ {failCount} failed</div>}
          {okCount === 0 && failCount === 0 && <div style={{ color: "#999" }}>—</div>}
        </div>
      </div>
    </div>
    {!isLast && <ConnectorArrow />}
  </div>
);

// ── Flow Diagram — Target grid card ────────────────────────────────────────

const TargetGridCard = ({ target }) => {
  const cfg = STATUS_CONFIG[target.status] || STATUS_CONFIG.error;
  const displayName = target.asset_name || target.repository_name || "—";
  const displaySub  = target.asset_ip  || (target.provider ? target.provider.replace(/_/g, " ") : "");
  return (
    <Tooltip title={target.error_message || ""} placement="top" arrow disableHoverListener={!target.error_message}>
      <div style={{
        background: "#fff",
        border: "1px solid #eef0f8",
        borderLeft: `3px solid ${cfg.color}`,
        borderRadius: 8,
        padding: "10px 14px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        cursor: target.error_message ? "help" : "default",
        transition: "box-shadow 0.15s",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
              {getTargetIcon(target)}
              <span style={{ fontWeight: 700, fontSize: 13, color: "#222", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {displayName}
              </span>
            </div>
            {displaySub && (
              <div style={{ fontSize: 11, color: "#888", marginBottom: 6, textTransform: "capitalize" }}>{displaySub}</div>
            )}
            <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#777", flexWrap: "wrap" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <MdFolder size={11} />{target.files_scanned ?? 0} files
              </span>
              {target.findings_count > 0 && (
                <span style={{ color: "#c62828", fontWeight: 700 }}>⚑ {target.findings_count} findings</span>
              )}
              {target.duration_seconds != null && (
                <span style={{ color: "#aaa" }}>{_fmt(target.duration_seconds)}</span>
              )}
            </div>
          </div>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            background: cfg.bg, color: cfg.color,
            border: `1px solid ${cfg.color}33`,
            padding: "3px 8px", borderRadius: 4,
            fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
          }}>
            <cfg.Icon size={11} />
            {cfg.label}
          </span>
        </div>
        {target.error_message && (
          <div style={{
            marginTop: 6, fontSize: 11, color: "#c62828",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            borderTop: "1px solid rgba(198,40,40,0.12)", paddingTop: 5,
          }}>
            ⚠ {target.error_message.slice(0, 90)}{target.error_message.length > 90 ? "…" : ""}
          </div>
        )}
      </div>
    </Tooltip>
  );
};

// ── Flow Diagram — Category section ────────────────────────────────────────

const TargetSection = ({ label, icon, color, targets }) => {
  const counts = countStatus(targets);
  const ok   = (counts.success || 0) + (counts.no_files || 0) + (counts.no_credentials || 0);
  const fail = targets.length - ok;
  return (
    <div style={{ background: "#fafbff", border: "1px solid #e8ecf8", borderRadius: 12, overflow: "hidden" }}>
      <div style={{
        background: color, color: "#fff",
        padding: "10px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 13 }}>
          {icon}
          {label}
        </div>
        <div style={{ display: "flex", gap: 6, fontSize: 11 }}>
          <span style={{ background: "rgba(255,255,255,0.18)", padding: "2px 9px", borderRadius: 10 }}>
            {targets.length} total
          </span>
          {ok > 0 && (
            <span style={{ background: "rgba(255,255,255,0.18)", padding: "2px 9px", borderRadius: 10, color: "#c8e6c9" }}>
              ✓ {ok}
            </span>
          )}
          {fail > 0 && (
            <span style={{ background: "rgba(255,255,255,0.18)", padding: "2px 9px", borderRadius: 10, color: "#ffcdd2" }}>
              ✗ {fail}
            </span>
          )}
        </div>
      </div>
      <div style={{ padding: 14, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
        {targets.map((t) => <TargetGridCard key={t.id} target={t} />)}
      </div>
    </div>
  );
};

// ── Flow Diagram ────────────────────────────────────────────────────────────

function FlowDiagram({ scan, targets }) {
  const { t } = useTranslation();
  const { pamGroups, gitGroups } = useMemo(() => groupTargets(targets), [targets]);

  if (!targets.length) {
    return (
      <div style={{ textAlign: "center", color: "#999", padding: "60px 0 40px" }}>
        <MdInfo size={40} style={{ marginBottom: 12, opacity: 0.35 }} />
        <div style={{ fontSize: 15, marginBottom: 6 }}>{t("credentialScanning.noScanTargetData")}</div>
        <small style={{ color: "#bbb" }}>{t("credentialScanning.targetTrackingNote")}</small>
      </div>
    );
  }

  const pamTargets = Object.values(pamGroups).flat();
  const gitTargets = Object.values(gitGroups).flat();
  const hasPam = pamTargets.length > 0;
  const hasGit = gitTargets.length > 0;

  // Build pipeline stages
  const pamStages = Object.entries(pamGroups).map(([type, tgts]) => {
    const cfg = PIPELINE_TYPE_CONFIG[type] || { label: type.toUpperCase(), color: "#546e7a", Icon: FaServer };
    const c = countStatus(tgts);
    const ok = (c.success || 0) + (c.no_files || 0) + (c.no_credentials || 0);
    return { ...cfg, label: resolveLabel(cfg, t), key: type, count: tgts.length, okCount: ok, failCount: tgts.length - ok };
  });

  const gitStages = Object.entries(gitGroups).map(([provider, tgts]) => {
    const cfg = PIPELINE_TYPE_CONFIG[provider] || { label: provider.replace(/_/g, " "), color: "#546e7a", Icon: FaGithub };
    const c = countStatus(tgts);
    const ok = (c.success || 0) + (c.no_files || 0) + (c.no_credentials || 0);
    return { ...cfg, label: resolveLabel(cfg, t), key: provider, count: tgts.length, okCount: ok, failCount: tgts.length - ok };
  });

  const allStages = [...pamStages, ...gitStages];

  return (
    <div style={{ padding: "0 25px 32px" }}>

      {/* Pipeline flow bar */}
      <div style={{
        background: "linear-gradient(135deg, #f5f7ff 0%, #eef2ff 100%)",
        border: "1px solid #dde3ff",
        borderRadius: 14,
        padding: "20px 24px",
        marginBottom: 24,
        overflowX: "auto",
      }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: "#7986cb", fontWeight: 700, textTransform: "uppercase", marginBottom: 16 }}>
          {t("credentialScanning.scanPipeline")}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 0, flexWrap: "nowrap", minWidth: "fit-content" }}>

          {/* Root config node */}
          <div style={{
            background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
            color: "#fff",
            borderRadius: 12,
            padding: "14px 22px",
            minWidth: 170,
            boxShadow: "0 4px 16px rgba(26,35,126,0.28)",
            flexShrink: 0,
          }}>
            <div style={{ fontSize: 9, letterSpacing: 2, opacity: 0.55, marginBottom: 5, textTransform: "uppercase" }}>{t("credentialScanning.scanConfig")}</div>
            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 10, lineHeight: 1.3 }}>
              {scan.config_name || t("credentialScanning.scanHash", { id: scan.id })}
            </div>
            <div style={{ display: "flex", gap: 14, fontSize: 10 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 17, lineHeight: 1 }}>{targets.length}</div>
                <div style={{ opacity: 0.55, marginTop: 2 }}>{t("credentialScanning.targetsLabel")}</div>
              </div>
              <div style={{ borderLeft: "1px solid rgba(255,255,255,0.2)", paddingLeft: 14 }}>
                <div style={{ fontWeight: 800, fontSize: 17, lineHeight: 1, color: scan.total_findings > 0 ? "#ffcdd2" : "#fff" }}>
                  {scan.total_findings ?? 0}
                </div>
                <div style={{ opacity: 0.55, marginTop: 2 }}>{t("credentialScanning.findingsLabel")}</div>
              </div>
            </div>
          </div>

          <ConnectorArrow />

          {/* Stage nodes */}
          {allStages.map((stage, i) => (
            <PipelineStageNode
              key={stage.key}
              label={stage.label}
              Icon={stage.Icon}
              color={stage.color}
              count={stage.count}
              okCount={stage.okCount}
              failCount={stage.failCount}
              isLast={i === allStages.length - 1}
            />
          ))}
        </div>
      </div>

      {/* Target sections grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: hasPam && hasGit ? "1fr 1fr" : "1fr",
        gap: 20,
      }}>
        {hasPam && (
          <TargetSection
            label={t("credentialScanning.pamAssets")}
            icon={<FaServer size={13} />}
            color="#1565c0"
            targets={pamTargets}
          />
        )}
        {hasGit && (
          <TargetSection
            label={t("credentialScanning.gitRepositories")}
            icon={<MdCode size={13} />}
            color="#37474f"
            targets={gitTargets}
          />
        )}
      </div>

      {/* Status legend */}
      <div className="cred-scan-flow-legend" style={{ marginTop: 20 }}>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const count = targets.filter((tg) => tg.status === key).length;
          if (!count) return null;
          return (
            <span key={key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <cfg.Icon size={13} style={{ color: cfg.color }} />
              <strong style={{ color: cfg.color }}>{count}</strong>
              <span>{resolveLabel(cfg, t)}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Target list tab ─────────────────────────────────────────────────────────

function TargetList({ targets }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const filtered = useMemo(() => {
    let result = targets;
    if (filterStatus) result = result.filter((t) => t.status === filterStatus);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (t) =>
          (t.asset_name || "").toLowerCase().includes(q) ||
          (t.repository_name || "").toLowerCase().includes(q) ||
          (t.asset_ip || "").toLowerCase().includes(q) ||
          (t.provider || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [targets, search, filterStatus]);

  const statusOptions = useMemo(() => [...new Set(targets.map((t) => t.status))], [targets]);

  const columns = [
    {
      name: t("credentialScanning.tgtColType"),
      width: "110px",
      cell: (row) => (
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          {getTargetIcon(row)}
          <span style={{ textTransform: "capitalize", color: "#555" }}>
            {row.target_type === "git_repo" ? (row.provider || "git") : row.target_type}
          </span>
        </span>
      ),
    },
    {
      name: t("credentialScanning.tgtColName"),
      grow: 2,
      cell: (row) => (
        <span style={{ fontWeight: 600, fontSize: 13 }}>
          {row.asset_name || row.repository_name || "—"}
        </span>
      ),
    },
    {
      name: t("credentialScanning.tgtColHostUrl"),
      grow: 1,
      cell: (row) => (
        <span style={{ fontSize: 12, color: "#666" }}>{row.asset_ip || row.provider || "—"}</span>
      ),
    },
    {
      name: t("credentialScanning.tgtColStatus"),
      width: "165px",
      cell: (row) => <StatusChip statusKey={row.status} />,
    },
    {
      name: t("credentialScanning.tgtColFiles"),
      width: "75px",
      right: true,
      cell: (row) => <span style={{ fontSize: 13 }}>{row.files_scanned ?? 0}</span>,
    },
    {
      name: t("credentialScanning.tgtColFindings"),
      minWidth: "110px",
      right: true,
      cell: (row) =>
        row.findings_count > 0 ? (
          <span style={{ fontSize: 13, fontWeight: 700, color: "#c62828" }}>{row.findings_count}</span>
        ) : (
          <span style={{ fontSize: 13, color: "#aaa" }}>0</span>
        ),
    },
    {
      name: t("credentialScanning.tgtColDuration"),
      minWidth: "110px",
      cell: (row) => <span style={{ fontSize: 12, color: "#666" }}>{_fmt(row.duration_seconds)}</span>,
    },
    {
      name: t("credentialScanning.tgtColErrorReason"),
      grow: 2,
      minWidth: "220px",
      wrap: true,
      cell: (row) =>
        row.error_message ? (
          <Tooltip title={row.error_message} placement="top" arrow>
            <span style={{ fontSize: 12, color: "#c62828", cursor: "help", display: "block", width: "100%" }}>
              {row.error_message}
            </span>
          </Tooltip>
        ) : (
          <span style={{ fontSize: 12, color: "#bbb" }}>—</span>
        ),
    },
  ];

  // NOTE: Intentionally NOT using className="rdt_container" here.
  // rdt_container has max-height: 0vh which collapses this table since
  // its parent has no fixed height (unlike full-page DataTables).
  // Padding is applied via the wrapper div instead.
  return (
    <div style={{ padding: "0 25px 3rem", backgroundColor: "var(--theme-background-paper, #fff)" }}>
      <div className="cred-scan-filter-bar">
        <input
          type="text"
          placeholder={t("credentialScanning.searchByNameHostProvider")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">{t("credentialScanning.allStatuses")}</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>{resolveLabel(STATUS_CONFIG[s], t) || s}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: "#999" }}>
          {t("credentialScanning.filteredOfTotalTargets", { filtered: filtered.length, total: targets.length })}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        noDataComponent={t("credentialScanning.noTargetsFound")}
        highlightOnHover
        pagination
        paginationPerPage={20}
        paginationRowsPerPageOptions={[10, 20, 50]}
      />
    </div>
  );
}

// ── Files Scanned tab ──────────────────────────────────────────────────────

const SOURCE_TYPE_KEY = {
  ssh_asset: "credentialScanning.pipelineSshSftp",
  rdp_asset: "credentialScanning.pipelineWindowsSmb",
  database:  "credentialScanning.sourceDatabase",
  git_repo:  "credentialScanning.gitShort",
};

const SOURCE_TYPE_ICON = {
  ssh_asset: <FaServer   size={13} style={{ color: "#1565c0" }} />,
  rdp_asset: <FaWindows  size={13} style={{ color: "#6a1b9a" }} />,
  database:  <FaDatabase size={13} style={{ color: "#1b5e20" }} />,
  git_repo:  <FaGithub   size={13} style={{ color: "#24292e" }} />,
};

const _fmtSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function ScannedFilesList({ scanId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows,     setRows]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [page,     setPage]     = useState(1);
  const [perPage,  setPerPage]  = useState(25);
  const [sourceType,        setSourceType]        = useState("");
  const [withFindingsOnly,  setWithFindingsOnly]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await getScannedFiles({
      scan_id: scanId,
      source_type: sourceType || undefined,
      search: search || undefined,
      with_findings_only: withFindingsOnly,
      page,
      page_size: perPage,
    });
    if (data) {
      setRows(data.scanned_files || []);
      setTotal(data.total || 0);
    }
    setLoading(false);
  }, [scanId, sourceType, search, withFindingsOnly, page, perPage]);

  useEffect(() => { load(); }, [load]);

  // Click → navigate to Findings page filtered to this (scan, file_path).
  // location.state is consumed by Findings.js (filterScanId / filterFilePath).
  const handleRowClick = (row) => {
    navigate("/dashboard/discovery/hardcoded-credentials/findings", {
      state: { scanId: scanId, filePath: row.file_path },
    });
  };

  const columns = [
    {
      name: t("credentialScanning.tgtColType"),
      width: "130px",
      cell: (row) => (
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          {SOURCE_TYPE_ICON[row.source_type] || <MdCode size={13} />}
          <span style={{ color: "#555" }}>{SOURCE_TYPE_KEY[row.source_type] ? t(SOURCE_TYPE_KEY[row.source_type]) : row.source_type}</span>
        </span>
      ),
    },
    {
      name: t("credentialScanning.fileColAssetSource"),
      grow: 1,
      minWidth: "160px",
      cell: (row) => (
        <span style={{ fontSize: 13, color: "#444" }}>{row.asset_name || "—"}</span>
      ),
    },
    {
      name: t("credentialScanning.fileColPath"),
      grow: 3,
      minWidth: "320px",
      wrap: true,
      cell: (row) => (
        <Tooltip title={row.file_path} placement="top" arrow>
          <span
            style={{
              fontSize: 12,
              color: "#1a237e",
              cursor: "pointer",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              wordBreak: "break-all",
            }}
            onClick={() => handleRowClick(row)}
          >
            {row.file_path}
          </span>
        </Tooltip>
      ),
    },
    {
      name: t("credentialScanning.fileColSize"),
      width: "100px",
      right: true,
      cell: (row) => <span style={{ fontSize: 12, color: "#777" }}>{_fmtSize(row.file_size)}</span>,
    },
    {
      name: t("credentialScanning.tgtColFindings"),
      width: "110px",
      right: true,
      cell: (row) =>
        row.findings_count > 0 ? (
          <span style={{ fontSize: 13, fontWeight: 700, color: "#c62828" }}>{row.findings_count}</span>
        ) : (
          <span style={{ fontSize: 13, color: "#aaa" }}>0</span>
        ),
    },
    {
      name: t("credentialScanning.fileColScanned"),
      width: "170px",
      cell: (row) => (
        <span style={{ fontSize: 12, color: "#666" }}>
          {row.scanned_at ? new Date(row.scanned_at).toLocaleString() : "—"}
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: "0 25px 3rem", backgroundColor: "var(--theme-background-paper, #fff)" }}>
      <div className="cred-scan-filter-bar">
        <input
          type="text"
          placeholder={t("credentialScanning.searchByFilePath")}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select value={sourceType} onChange={(e) => { setSourceType(e.target.value); setPage(1); }}>
          <option value="">{t("credentialScanning.allSources")}</option>
          <option value="ssh_asset">{t("credentialScanning.pipelineSshSftp")}</option>
          <option value="rdp_asset">{t("credentialScanning.pipelineWindowsSmb")}</option>
          <option value="database">{t("credentialScanning.sourceDatabase")}</option>
          <option value="git_repo">{t("credentialScanning.sourceGitRepository")}</option>
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#666" }}>
          <input
            type="checkbox"
            checked={withFindingsOnly}
            onChange={(e) => { setWithFindingsOnly(e.target.checked); setPage(1); }}
          />
          {t("credentialScanning.onlyFilesWithFindings")}
        </label>
        <span style={{ fontSize: 12, color: "#999" }}>{t("credentialScanning.totalFilesShort", { count: total })}</span>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        progressPending={loading}
        noDataComponent={t("credentialScanning.noFilesScannedInRun")}
        highlightOnHover
        pointerOnHover
        onRowClicked={handleRowClick}
        pagination
        paginationServer
        paginationTotalRows={total}
        paginationDefaultPage={page}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        onChangePage={(p) => setPage(p)}
        onChangeRowsPerPage={(n) => { setPerPage(n); setPage(1); }}
      />
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

function ScanDetailPage() {
  const { t } = useTranslation();
  const { scanId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [slideChecked, setSlideChecked] = useState(false);

  // Scan stub passed via navigation state for immediate display while loading
  const passedScan = location.state?.scan || null;

  useEffect(() => {
    dispatch(changeTitle("Discovery"));
    document.title = t("credentialScanning.scanDetails");

    setLoading(true);
    setError(null);
    getScanDetails(scanId).then(({ data, error: err }) => {
      if (err) {
        setError(t("credentialScanning.toastFailedToLoadScanDetails"));
      } else {
        setDetails(data);
      }
      setLoading(false);
      setSlideChecked(true);
    });
  }, [scanId, dispatch, t]);

  const scan = details?.scan || passedScan;
  const targets = details?.targets || [];

  const statBar = !loading && targets.length > 0
    ? [
        { label: t("credentialScanning.statTotalTargets"),  value: targets.length,                                                                                          color: "#1a237e" },
        { label: t("credentialScanning.statFilesScanned"),  value: targets.reduce((s, tg) => s + (tg.files_scanned || 0), 0),                                              color: "#1565c0" },
        { label: t("credentialScanning.statFindings"),      value: targets.reduce((s, tg) => s + (tg.findings_count || 0), 0),                                             color: "#c62828" },
        { label: t("credentialScanning.statSuccessful"),    value: targets.filter((tg) => tg.status === "success").length,                                                  color: "#2e7d32" },
        { label: t("credentialScanning.statFailedErrors"),  value: targets.filter((tg) => ["connection_failed", "auth_failed", "error"].includes(tg.status)).length,        color: "#c62828" },
        { label: t("credentialScanning.statNoCredentials"), value: targets.filter((tg) => tg.status === "no_credentials").length,                                           color: "#e65100" },
      ]
    : null;

  return (
    <>
      {loading && !details ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
          <div className="la-line-scale-pulse-out-rapid la-3x" style={{ color: "#5177ff" }}>
            <div className="rounded" /><div className="rounded" /><div className="rounded" />
            <div className="rounded" /><div className="rounded" />
          </div>
        </div>
      ) : (
        <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
          <Fade timeout={fadedelayTime} in={slideChecked}>
            <div className="main_content_container p-3 w-100">
              <div className="position-relative bg-white" style={{ minHeight: "calc(100vh - 120px)" }}>

                <div style={{ padding: "0 25px" }}>
                  {/* Header section */}
                  <div className="py-2 pt-2">
                    <BreadcrumbHeading
                      dataTableHeadingText={[
                        t("credentialScanning.breadcrumbBackTo"),
                        t("credentialScanning.scanHistory"),
                        t("credentialScanning.scanDetails"),
                      ]}
                      dataTablePageLink={[
                        "/dashboard/discovery/hardcoded-credentials",
                        "/dashboard/discovery/hardcoded-credentials/scan-history",
                        `/dashboard/discovery/hardcoded-credentials/scan-history/${scanId}/details`,
                      ]}
                      pageNumber={5}
                    />
                  </div>

                  <div className="mb-3">
                    <ListHeading
                      dataTableHeadingText={
                        scan
                          ? t("credentialScanning.scanDetailsHeading", { name: scan.config_name || t("credentialScanning.scanHash", { id: scan.id }) })
                          : t("credentialScanning.scanDetails")
                      }
                      dataTableHeadingIcon={Images.NetworkAnalyzer}
                    />
                  </div>

                  {/* Scan meta row */}
                  {scan && (
                    <div className="cred-scan-meta-row">
                      {(() => {
                        const s = SCAN_STATUS_STYLES[scan.status] || {};
                        return (
                          <span style={{ background: s.bg, color: s.color, padding: "4px 12px", borderRadius: 4, fontSize: 13, fontWeight: 700 }}>
                            {scan.status?.replace("_", " ")}
                          </span>
                        );
                      })()}
                      {scan.started_at && (
                        <span>{t("credentialScanning.metaStarted", { time: new Date(scan.started_at).toLocaleString() })}</span>
                      )}
                      {scan.duration_seconds != null && (
                        <span>{t("credentialScanning.metaDuration", { value: _fmt(scan.duration_seconds) })}</span>
                      )}
                      <span>
                        {t("credentialScanning.metaTriggeredBy", { user: scan.triggered_by_username || "—", type: scan.triggered_type || "—" })}
                      </span>
                    </div>
                  )}

                  {error ? (
                    <div style={{ color: "#c62828", padding: "32px 0", textAlign: "center" }}>
                      {error}
                    </div>
                  ) : (
                    <>
                      {/* Stat bar */}
                      {statBar && (
                        <div className="cred-scan-stat-bar">
                          {statBar.map(({ label, value, color }) => (
                            <div key={label} className="cred-scan-stat-cell">
                              <div className="stat-value" style={{ color }}>{value}</div>
                              <div className="stat-label">{label}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Tabs */}
                      <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        sx={{
                          borderBottom: "1px solid #eee",
                          mb: 1,
                          "& .MuiTab-root": { minHeight: 44, fontSize: 13, textTransform: "none", fontWeight: 600 },
                          "& .Mui-selected": { color: "#5177ff" },
                          "& .MuiTabs-indicator": { backgroundColor: "#5177ff" },
                        }}
                      >
                        <Tab label={t("credentialScanning.tabFlowDiagram")} />
                        <Tab label={t("credentialScanning.tabTargetList")} />
                        <Tab label={t("credentialScanning.tabFilesScanned")} />
                      </Tabs>
                    </>
                  )}
                </div>

                {!error && (
                  <>
                    {tab === 0 && <FlowDiagram scan={scan || {}} targets={targets} />}
                    {tab === 1 && <TargetList targets={targets} />}
                    {tab === 2 && <ScannedFilesList scanId={scanId} />}
                  </>
                )}
              </div>
            </div>
          </Fade>
        </Slide>
      )}
    </>
  );
}

export default ScanDetailPage;
